import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import organizationApi from '../../services/api/organization'

export interface Organization {
  id: string
  uuid: string
  name: string
  description?: string
  email?: string
  phone?: string
  website?: string
  address?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  logo?: string
  settings?: any
  metadata?: any
  isActive: boolean
  provider?: string
  externalId?: string
  userCount?: number
  createdAt: string
  updatedAt: string
}

export interface OrganizationStats {
  total: number
  active: number
  inactive: number
  totalUsers: number
  thisMonth: number
  growth: number
}

interface OrganizationFilters {
  status?: string
  provider?: string
  sortBy?: string
  dateRange?: string
}

interface OrganizationsState {
  organizations: Organization[]
  selectedOrganization: Organization | null
  loading: boolean
  error: string | null
  totalCount: number
  page: number
  pageSize: number
  stats: OrganizationStats | null
  filters: OrganizationFilters
}

const initialState: OrganizationsState = {
  organizations: [],
  selectedOrganization: null,
  loading: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: 20,
  stats: null,
  filters: {},
}

// Async thunks
export const fetchOrganizations = createAsyncThunk(
  'organizations/fetchAll',
  async ({ page, pageSize }: { page: number; pageSize: number }) => {
    const response = await organizationApi.getOrganizations(page, pageSize)
    return response.data
  }
)

export const fetchOrganizationById = createAsyncThunk(
  'organizations/fetchById',
  async (organizationId: string) => {
    const response = await organizationApi.getOrganizationById(organizationId)
    return response.data
  }
)

export const fetchOrganizationStats = createAsyncThunk(
  'organizations/fetchStats',
  async () => {
    const response = await organizationApi.getOrganizationStats()
    return response.data
  }
)

export const searchOrganizations = createAsyncThunk(
  'organizations/search',
  async (query: string) => {
    const response = await organizationApi.searchOrganizations(query)
    return response.data
  }
)

export const createOrganization = createAsyncThunk(
  'organizations/create',
  async (organizationData: Partial<Organization>) => {
    const response = await organizationApi.createOrganization(organizationData)
    return response.data
  }
)

export const updateOrganization = createAsyncThunk(
  'organizations/update',
  async ({ id, data }: { id: string; data: Partial<Organization> }) => {
    const response = await organizationApi.updateOrganization(id, data)
    return response.data
  }
)

export const deleteOrganization = createAsyncThunk(
  'organizations/delete',
  async (organizationId: string) => {
    await organizationApi.deleteOrganization(organizationId)
    return organizationId
  }
)

export const activateOrganization = createAsyncThunk(
  'organizations/activate',
  async (organizationId: string) => {
    const response = await organizationApi.activateOrganization(organizationId)
    return response.data
  }
)

export const deactivateOrganization = createAsyncThunk(
  'organizations/deactivate',
  async (organizationId: string) => {
    const response = await organizationApi.deactivateOrganization(organizationId)
    return response.data
  }
)

export const fetchOrganizationUsers = createAsyncThunk(
  'organizations/fetchUsers',
  async ({ organizationId, page = 1, pageSize = 10 }: { organizationId: string; page?: number; pageSize?: number }) => {
    const response = await organizationApi.getOrganizationUsers(organizationId, page, pageSize)
    return response.data
  }
)

export const fetchOrganizationBranches = createAsyncThunk(
  'organizations/fetchBranches',
  async (organizationId: string) => {
    const response = await organizationApi.getOrganizationBranches(organizationId)
    return response.data
  }
)

// Slice
const organizationsSlice = createSlice({
  name: 'organizations',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<OrganizationFilters>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    clearError: (state) => {
      state.error = null
    },
    setSelectedOrganization: (state, action: PayloadAction<Organization | null>) => {
      state.selectedOrganization = action.payload
    },
  },
  extraReducers: (builder) => {
    // Fetch organizations
    builder
      .addCase(fetchOrganizations.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchOrganizations.fulfilled, (state, action) => {
        state.loading = false
        state.organizations = action.payload.data
        state.totalCount = action.payload.total
        state.page = action.payload.page
        state.pageSize = action.payload.pageSize
      })
      .addCase(fetchOrganizations.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch organizations'
      })

    // Fetch organization by ID
    builder
      .addCase(fetchOrganizationById.pending, (state) => {
        state.loading = true
      })
      .addCase(fetchOrganizationById.fulfilled, (state, action) => {
        state.loading = false
        state.selectedOrganization = action.payload
      })
      .addCase(fetchOrganizationById.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch organization'
      })

    // Fetch stats
    builder
      .addCase(fetchOrganizationStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
      .addCase(fetchOrganizationStats.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch stats'
      })

    // Search organizations
    builder
      .addCase(searchOrganizations.pending, (state) => {
        state.loading = true
      })
      .addCase(searchOrganizations.fulfilled, (state, action) => {
        state.loading = false
        state.organizations = action.payload.data
        state.totalCount = action.payload.total
      })
      .addCase(searchOrganizations.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Search failed'
      })

    // Create organization
    builder
      .addCase(createOrganization.pending, (state) => {
        state.loading = true
      })
      .addCase(createOrganization.fulfilled, (state, action) => {
        state.loading = false
        state.organizations.unshift(action.payload)
        state.totalCount += 1
      })
      .addCase(createOrganization.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to create organization'
      })

    // Update organization
    builder
      .addCase(updateOrganization.fulfilled, (state, action) => {
        const index = state.organizations.findIndex(org => org.id === action.payload.id)
        if (index !== -1) {
          state.organizations[index] = action.payload
        }
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization = action.payload
        }
      })
      .addCase(updateOrganization.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update organization'
      })

    // Delete organization
    builder
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        state.organizations = state.organizations.filter(org => org.id !== action.payload)
        state.totalCount -= 1
        if (state.selectedOrganization?.id === action.payload) {
          state.selectedOrganization = null
        }
      })
      .addCase(deleteOrganization.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete organization'
      })

    // Activate organization
    builder
      .addCase(activateOrganization.fulfilled, (state, action) => {
        const index = state.organizations.findIndex(org => org.id === action.payload.id)
        if (index !== -1) {
          state.organizations[index] = { ...state.organizations[index], isActive: true }
        }
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization.isActive = true
        }
      })
      .addCase(activateOrganization.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to activate organization'
      })

    // Deactivate organization
    builder
      .addCase(deactivateOrganization.fulfilled, (state, action) => {
        const index = state.organizations.findIndex(org => org.id === action.payload.id)
        if (index !== -1) {
          state.organizations[index] = { ...state.organizations[index], isActive: false }
        }
        if (state.selectedOrganization?.id === action.payload.id) {
          state.selectedOrganization.isActive = false
        }
      })
      .addCase(deactivateOrganization.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to deactivate organization'
      })
  },
})

export const {
  setFilters,
  clearFilters,
  clearError,
  setSelectedOrganization,
} = organizationsSlice.actions

export default organizationsSlice.reducer